import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import AnimatedBackground from '../components/AnimatedBackground';
import { 
  Music, 
  Mic, 
  Wand2, 
  AlertCircle, 
  X,
  Upload,
  Download,
  Volume2,
  FileAudio,
  SlidersHorizontal,
  ArrowRight,
  Check,
  Zap,
  Shield,
  Clock,
  Heart,
  Sparkles,
  Play
} from 'lucide-react';

const ERROR_MESSAGES = {
  'auth_failed': 'Authentication failed. Please try again.',
  'no_code': 'Authentication was cancelled. Please try again.',
  'state_mismatch': 'Security verification failed. Please try again.',
  'missing_verifier': 'Session expired. Please try again.',
  'database_not_configured': 'Service temporarily unavailable. Please try again later.',
  'callback_failed': 'Something went wrong during sign in. Please try again.',
  'user_not_found': 'No account found with this email. Please sign up first.',
  'already_exists': 'An account with this email already exists. Please log in instead.',
};

export default function Home() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(ERROR_MESSAGES[errorParam] || 'An error occurred. Please try again.');
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('error');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const dismissError = () => {
    setError(null);
  };

  const handleSignUp = () => {
    window.location.href = '/api/auth/login?mode=signup';
  };

  const handleLogin = () => {
    window.location.href = '/api/auth/login';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0a0a0f] via-[#0d0d14] to-[#0a0a0f]">
        <img src="/logo.png" alt="Loading" className="h-16 w-16 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#0d0d14] to-[#0a0a0f] relative">
      <AnimatedBackground />
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/30 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-end h-16">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={handleLogin}
                className="text-gray-300 hover:text-white hover:bg-white/5"
              >
                Log In
              </Button>
              <Button 
                onClick={handleSignUp}
                className="bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90 shadow-lg shadow-primary/20"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {error && (
        <div className="fixed top-20 left-0 right-0 z-50 px-4">
          <div className="max-w-md mx-auto">
            <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 shadow-lg">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-4">
                <span>{error}</span>
                <button 
                  onClick={dismissError}
                  className="text-red-400 hover:text-red-300 transition-colors flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </button>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      )}

      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-24 px-4">
          <div className="max-w-5xl mx-auto text-center">
            <div className="flex flex-col items-center mb-10">
              <img src="/logo.png" alt="Allioki" className="h-36 w-36 sm:h-44 sm:w-44 mb-6" />
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-400 to-pink-400 tracking-widest">
                ALLIOKI
              </h1>
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-purple-500/20 border border-primary/30 text-primary text-sm font-medium mb-8">
              <Sparkles className="h-4 w-4" />
              Turn Any Song Into Your Personal Karaoke Track
            </div>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Sing Your Heart Out
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-400 to-pink-400">
                With Any Song You Love
              </span>
            </h2>
            
            <p className="text-lg sm:text-xl text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed">
              Allioki transforms your favorite songs into karaoke experiences. 
              We remove vocals, sync lyrics perfectly, and let you sing along like a star. 
              It's karaoke night, anytime you want.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Button 
                size="lg" 
                onClick={handleSignUp}
                className="w-full sm:w-auto px-8 py-6 text-lg bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90 shadow-xl shadow-primary/25 group"
              >
                <Play className="mr-2 h-5 w-5" />
                Start Singing Free
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={handleLogin}
                className="w-full sm:w-auto px-8 py-6 text-lg border-gray-700 text-gray-300 hover:bg-white/5 hover:border-gray-600"
              >
                I Have an Account
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
                No credit card
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
                Free to start
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
                Any song works
              </span>
            </div>
          </div>
        </section>

        {/* 3-Step Process Section */}
        <section className="py-24 px-4 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                From Song to Karaoke in 3 Easy Steps
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                No complicated setup, no expensive equipment. Just upload, and we handle the magic.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <StepCard
                number="1"
                icon={<Upload className="h-8 w-8" />}
                title="Upload Your Song"
                description="Drop any song file and we'll take care of the rest. MP3, WAV, M4A, and more formats supported."
                features={['All major formats', 'Up to 15 minutes', 'High quality preserved']}
              />
              <StepCard
                number="2"
                icon={<Wand2 className="h-8 w-8" />}
                title="AI Works Its Magic"
                description="Our AI separates the vocals, finds the lyrics, and syncs everything perfectly to the beat."
                features={['Vocal removal', 'Lyrics sync', 'Beat detection']}
              />
              <StepCard
                number="3"
                icon={<Mic className="h-8 w-8" />}
                title="Sing Along!"
                description="Your karaoke track is ready. Follow the bouncing lyrics and belt out your favorite tunes."
                features={['Synced lyrics display', 'Adjustable pitch', 'Save your favorites']}
              />
            </div>
          </div>
        </section>

        {/* Features Grid Section */}
        <section className="py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 text-purple-400 text-sm font-medium mb-4">
                <Heart className="h-4 w-4" />
                Built for Music Lovers
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Everything You Need for the Perfect Karaoke Night
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Whether you're practicing for a performance or just having fun, 
                Allioki has the tools to make you sound amazing.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FeatureCard
                icon={<Volume2 className="h-6 w-6" />}
                title="Crystal-Clear Vocal Removal"
                description="Our AI cleanly separates vocals from instrumentals, giving you a professional-quality backing track that sounds great."
                gradient="from-blue-500/20 to-cyan-500/20"
                iconColor="text-blue-400"
              />
              <FeatureCard
                icon={<FileAudio className="h-6 w-6" />}
                title="Perfectly Synced Lyrics"
                description="Lyrics appear word-by-word, timed to the music. Never lose your place again with our smart lyric synchronization."
                gradient="from-purple-500/20 to-pink-500/20"
                iconColor="text-purple-400"
              />
              <FeatureCard
                icon={<SlidersHorizontal className="h-6 w-6" />}
                title="Pitch & Key Adjustment"
                description="Song too high or low for your voice? Adjust the key up or down to find your perfect range without distortion."
                gradient="from-amber-500/20 to-orange-500/20"
                iconColor="text-amber-400"
              />
              <FeatureCard
                icon={<Download className="h-6 w-6" />}
                title="Download & Share"
                description="Export your karaoke tracks to use offline, share with friends, or take to your next party. It's your music, your way."
                gradient="from-emerald-500/20 to-teal-500/20"
                iconColor="text-emerald-400"
              />
            </div>
          </div>
        </section>

        {/* More Tools Section */}
        <section className="py-20 px-4 bg-white/[0.02] border-y border-white/5">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-primary text-sm font-medium mb-2">Plus More Powerful Tools</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-white/10 hover:border-white/20 transition-all">
                <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center text-blue-400 mb-4">
                  <Volume2 className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">Professional Backing Tracks</h3>
                <p className="text-gray-400 text-sm">Studio-quality instrumental versions with crystal-clear vocal removal</p>
              </div>
              <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-white/10 hover:border-white/20 transition-all">
                <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center text-purple-400 mb-4">
                  <FileAudio className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">Real-Time Lyrics Display</h3>
                <p className="text-gray-400 text-sm">Word-by-word lyrics that follow the beat, never lose your place again</p>
              </div>
              <div className="p-6 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-white/10 hover:border-white/20 transition-all">
                <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center text-amber-400 mb-4">
                  <SlidersHorizontal className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">Key & Pitch Control</h3>
                <p className="text-gray-400 text-sm">Transpose any song to your comfortable singing range without quality loss</p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-24 px-4 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <TrustCard
                icon={<Zap className="h-8 w-8" />}
                title="Ready in Minutes"
                description="Upload a song and start singing in under 2 minutes. No waiting around for slow processing."
              />
              <TrustCard
                icon={<Shield className="h-8 w-8" />}
                title="Your Music, Private"
                description="Your uploads are encrypted and never shared. Delete anytime. Your karaoke sessions stay yours."
              />
              <TrustCard
                icon={<Clock className="h-8 w-8" />}
                title="Sing Anytime"
                description="Available 24/7 on any device. Practice in the morning, party at night. Allioki is always ready."
              />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-3xl bg-gradient-to-r from-primary/30 via-purple-500/30 to-pink-500/30 p-px">
              <div className="rounded-3xl bg-[#0d0d14] p-12 text-center">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-purple-500 mb-6 shadow-xl shadow-primary/30">
                  <Mic className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Ready to Find Your Voice?
                </h2>
                <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
                  Join thousands of music lovers who've discovered their inner star with Allioki. 
                  Your next favorite karaoke moment is just a song away.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button 
                    size="lg" 
                    onClick={handleSignUp}
                    className="w-full sm:w-auto px-8 py-6 text-lg bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90 shadow-xl shadow-primary/25"
                  >
                    <Sparkles className="mr-2 h-5 w-5" />
                    Get Started Free
                  </Button>
                  <Button 
                    size="lg" 
                    variant="ghost" 
                    onClick={handleLogin}
                    className="w-full sm:w-auto px-8 py-6 text-lg text-gray-300 hover:text-white hover:bg-white/5"
                  >
                    Sign In
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Allioki" className="h-8 w-8" />
            <span className="text-xl font-bold bg-gradient-to-r from-primary via-purple-400 to-pink-400 bg-clip-text text-transparent tracking-widest">ALLIOKI</span>
          </div>
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Allioki. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function StepCard({ number, icon, title, description, features }) {
  return (
    <div className="relative">
      <div className="absolute -top-4 left-6 z-10">
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-primary/30">
          {number}
        </div>
      </div>
      <div className="pt-8 p-6 rounded-2xl bg-white/5 border border-white/10 h-full hover:bg-white/[0.07] hover:border-white/20 transition-all">
        <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center text-primary mb-5">
          {icon}
        </div>
        <h3 className="text-xl font-semibold text-white mb-3">{title}</h3>
        <p className="text-gray-400 text-sm mb-4 leading-relaxed">{description}</p>
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-gray-500">
              <Check className="h-4 w-4 text-primary flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description, gradient, iconColor }) {
  return (
    <div className={`p-6 rounded-2xl bg-gradient-to-br ${gradient} border border-white/10 hover:border-white/20 transition-all hover:scale-[1.02] cursor-default`}>
      <div className={`h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center ${iconColor} mb-4`}>
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

function TrustCard({ icon, title, description }) {
  return (
    <div className="p-6">
      <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center text-primary mx-auto mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
    </div>
  );
}
